import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { ActiveRole } from "@/lib/constants";

export type AgencyType = "traditional" | "business" | "luxury" | "leisure" | "mixed";
export type CountryCode = "IT" | "FR" | "DE" | "ES" | "GB";

export interface TopDestination {
  destination_id: string;
  name: string;
  country_code?: string;
}

export interface ClienteleMix {
  couples: number;
  families: number;
  business: number;
  luxury: number;
}

export interface AgencyProfile {
  agencyId?: string;
  agencyName: string;
  agencyType: AgencyType;
  homeCountry: CountryCode;
  topDestinations: TopDestination[];
  clienteleMix: ClienteleMix;
  budgetMin: number;
  budgetMax: number;
  preferredStars: number[];
  notes?: string;
  /**
   * Tipo di org attiva → inviato come `X-Active-Role` al BE.
   * Undefined fino a quando `useOnboardingBootstrap` non lo popola dal
   * payload di `GET /me/profile`.
   *
   * NON hardcodare un default: l'app è multi-role (OTA / TRAVEL_AGENCY /
   * DMC / BAD_BANK), un default errato fa fallire silenziosamente con 403
   * tutte le chiamate API parallele al bootstrap.
   */
  activeRole?: ActiveRole;
  externalId?: string;
  onboardingCompletedAt?: string;
}

const initialState: AgencyProfile = {
  agencyName: "",
  agencyType: "traditional",
  homeCountry: "IT",
  topDestinations: [],
  clienteleMix: { couples: 25, families: 25, business: 25, luxury: 25 },
  budgetMin: 50,
  budgetMax: 500,
  preferredStars: [3, 4],
  // activeRole intenzionalmente undefined: popolato dal bootstrap.
};

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    setProfile(state, action: PayloadAction<Partial<AgencyProfile>>) {
      Object.assign(state, action.payload);
    },
    completeOnboarding(state) {
      state.onboardingCompletedAt = new Date().toISOString();
    },
    resetProfile() {
      return initialState;
    },
    setActiveRole(state, action: PayloadAction<ActiveRole>) {
      state.activeRole = action.payload;
    },
    setExternalId(state, action: PayloadAction<string | undefined>) {
      state.externalId = action.payload;
    },
  },
});

export const { setProfile, completeOnboarding, resetProfile, setActiveRole, setExternalId } =
  profileSlice.actions;
export default profileSlice.reducer;
